import React, { useMemo, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import {
  jobIdsFromAsyncResponse,
  jobStatusLabel,
  toMediaUrl,
  waitForJob,
} from '../lib/jobsApi';
import { defaultImageModel, pickImageModel } from '../lib/imageModelCatalog';
import ImageModelPicker from '../components/image/ImageModelPicker';
import { UploadCloud, AlertCircle, Sparkles, Camera, X, Layers } from 'lucide-react';

type PreviewImage = {
  url: string;
  source: string;
};

const STYLE_TAGS = [
  { id: 'palette_neutral', label: 'Tons Neutros', icon: '⚪' },
  { id: 'palette_warm', label: 'Tons Quentes', icon: '🟠' },
  { id: 'palette_cool', label: 'Tons Frios', icon: '🔵' },
  { id: 'palette_dark', label: 'Dark Mode', icon: '⚫' },
  { id: 'furniture_modern', label: 'Moderna', icon: '🛋️' },
  { id: 'furniture_minimalist', label: 'Minimalista', icon: '✨' },
  { id: 'furniture_industrial', label: 'Industrial', icon: '🏭' },
  { id: 'furniture_rustic', label: 'Rústica', icon: '🪵' },
  { id: 'style_scandinavian', label: 'Escandinavo', icon: '❄️' },
  { id: 'style_japandi', label: 'Japandi', icon: '🎋' },
  { id: 'style_mediterranean', label: 'Mediterrâneo', icon: '🌊' },
  { id: 'style_brazilian', label: 'Contemporâneo BR', icon: '🌿' },
];

export default function VirtualStaging() {
  const { user } = useAuth();
  const [prompt, setPrompt] = useState<string>('');
  const [startFrameB64, setStartFrameB64] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingStep, setLoadingStep] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [previews, setPreviews] = useState<PreviewImage[]>([]);
  const [imageModelId, setImageModelId] = useState<string>(defaultImageModel().id);
  const [count, setCount] = useState<1 | 2>(1);
  const [creativityMode, setCreativityMode] = useState<'strict' | 'redesign'>('strict');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const selectedModel = useMemo(() => pickImageModel(imageModelId), [imageModelId]);
  const totalCredits = selectedModel.creditsPerImage * count;

  const handleTextareaInput = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  };

  const toggleTag = (tagId: string) => {
    setSelectedTags((prev) =>
      prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId],
    );
  };

  const handleFrameUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setStartFrameB64(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const getUserOrganizationId = async () => {
    if (!user) return null;
    const { data } = await supabase
      .from('organization_members')
      .select('org_id')
      .eq('user_id', user.id)
      .single();
    return data?.org_id;
  };

  const handleGenerate = async () => {
    if (!startFrameB64 || !prompt) {
      setError('Envie a foto do espaço vazio e descreva a decoração desejada.');
      return;
    }
    setLoading(true);
    setLoadingStep('Gerando propostas de decoração...');
    setError(null);
    setPreviews([]);

    try {
      const orgId = await getUserOrganizationId();
      if (!orgId) throw new Error('Você não está em nenhuma organização.');

      const { data, error: invokeError } = await supabase.functions.invoke('create-kling-preview', {
        body: {
          image_b64: startFrameB64,
          prompt,
          organization_id: orgId,
          combo_mode: count === 2 ? 'combo_4' : 'combo_2',
          count,
          image_model_id: imageModelId,
          creativity_mode: creativityMode,
          tags: selectedTags,
        },
      });
      if (invokeError) throw new Error(invokeError.message);
      if (data?.error) throw new Error(data.error);

      const ids = jobIdsFromAsyncResponse(data);
      if (data?.async && ids.length) {
        setLoadingStep('Na fila — acompanhe o progresso');
        const jobs = await Promise.all(
          ids.map((id) =>
            waitForJob(id, {
              timeoutMs: 15 * 60 * 1000,
              onStatus: (j) => setLoadingStep(jobStatusLabel(j)),
            }),
          ),
        );
        const label = data.model_label || selectedModel.label;
        const next: PreviewImage[] = [];
        for (const job of jobs) {
          const url = toMediaUrl(job.result_url);
          if (url) next.push({ url, source: label });
        }
        if (!next.length) throw new Error('Geração concluída sem imagem.');
        setPreviews(next);
      } else if (data?.success) {
        setPreviews(data.previews || []);
      } else {
        throw new Error('Resposta inesperada da geração.');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro na geração de imagens.';
      setError(message);
    } finally {
      setLoading(false);
      setLoadingStep('');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '1.25rem' }}>
      <h2 style={{ marginBottom: '1rem', color: '#fff', fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Sparkles color="#a855f7" /> Mobiliação Inteligente
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
        Envie a foto de um ambiente vazio, descreva a decoração e escolha o modelo. Geramos propostas de mobiliário no mesmo espaço.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
        <div className="glass-card" style={{ padding: '1rem', minHeight: '220px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Camera size={16} style={{ color: '#6366f1' }} /> Imagem Original
          </div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', background: 'rgba(0,0,0,0.2)', borderRadius: '10px' }}>
            {!startFrameB64 ? (
              <label style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', padding: '2rem', color: 'var(--text-muted)' }}>
                <UploadCloud size={32} style={{ opacity: 0.3 }} />
                <span style={{ fontSize: '0.8rem' }}>Upload do ambiente cru</span>
                <input type="file" accept="image/*" onChange={handleFrameUpload} style={{ display: 'none' }} />
              </label>
            ) : (
              <>
                <img src={startFrameB64} alt="Original" style={{ width: '100%', maxHeight: '280px', objectFit: 'contain', borderRadius: '10px' }} />
                <button onClick={() => setStartFrameB64('')} style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.65)', border: 'none', color: '#fff', borderRadius: '50%', width: 24, height: 24, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <X size={14} />
                </button>
              </>
            )}
          </div>
        </div>

        <div className="glass-card" style={{ padding: '1rem', display: 'flex', flexDirection: 'column' }}>
           <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#a855f7', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
             <Sparkles size={16} /> Instruções de Decoração
           </div>

           <div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: '1rem' }}>
             <textarea
                ref={textareaRef}
                rows={3}
                value={prompt}
                onChange={(e) => { setPrompt(e.target.value); handleTextareaInput(); }}
                placeholder="Exemplo: Sala minimalista com sofá branco, tapete felpudo e plantas na varanda. Estilo escandinavo."
                style={{
                  width: '100%', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '12px', padding: '0.85rem', color: '#fff', resize: 'none', fontSize: '0.85rem',
                  outline: 'none', transition: 'border-color 0.2s'
                }}
              />

              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setCreativityMode('strict')}
                  style={{
                    padding: '0.4rem 0.8rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                    background: creativityMode === 'strict' ? 'rgba(168,85,247,0.2)' : 'rgba(0,0,0,0.3)',
                    color: creativityMode === 'strict' ? '#c084fc' : 'var(--text-muted)',
                    border: `1px solid ${creativityMode === 'strict' ? '#a855f7' : 'rgba(255,255,255,0.1)'}`,
                    transition: 'all 0.2s'
                  }}
                >
                  Staging fiel (preserva as paredes)
                </button>
                <button
                  onClick={() => setCreativityMode('redesign')}
                  style={{
                    padding: '0.4rem 0.8rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                    background: creativityMode === 'redesign' ? 'rgba(56,189,248,0.2)' : 'rgba(0,0,0,0.3)',
                    color: creativityMode === 'redesign' ? '#7dd3fc' : 'var(--text-muted)',
                    border: `1px solid ${creativityMode === 'redesign' ? '#38bdf8' : 'rgba(255,255,255,0.1)'}`,
                    transition: 'all 0.2s'
                  }}
                >
                  Reforma livre (pode mudar paredes)
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Adicionar tags à descrição:</span>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  {STYLE_TAGS.map((tag) => {
                    const isSelected = selectedTags.includes(tag.id);
                    return (
                      <button
                        key={tag.id}
                        onClick={() => toggleTag(tag.id)}
                        style={{
                          padding: '0.3rem 0.6rem',
                          borderRadius: '16px',
                          fontSize: '0.7rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                          background: isSelected ? 'rgba(168, 85, 247, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                          color: isSelected ? '#c084fc' : 'var(--text-muted)',
                          border: `1px solid ${isSelected ? '#a855f7' : 'rgba(255, 255, 255, 0.1)'}`,
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.3rem'
                        }}
                      >
                        <span>{tag.icon}</span> {tag.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Modelo</span>
                <ImageModelPicker
                  aria-label="Modelo de imagem"
                  value={imageModelId}
                  onChange={setImageModelId}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Quantidade</span>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {([1, 2] as const).map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setCount(n)}
                      style={{
                        flex: 1,
                        padding: '0.5rem 0.75rem',
                        borderRadius: '10px',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        background: count === n ? 'rgba(168,85,247,0.2)' : 'rgba(0,0,0,0.3)',
                        color: count === n ? '#c084fc' : 'var(--text-muted)',
                        border: `1px solid ${count === n ? '#a855f7' : 'rgba(255,255,255,0.1)'}`,
                      }}
                    >
                      {n === 1 ? '1 proposta' : '2 propostas'}
                    </button>
                  ))}
                </div>
              </div>

              <button
                className="btn-primary"
                onClick={handleGenerate}
                disabled={loading || !startFrameB64 || !prompt}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 600, opacity: (loading || !startFrameB64 || !prompt) ? 0.5 : 1 }}
              >
                {count === 1
                  ? `Gerar proposta · ${totalCredits} créditos`
                  : `Gerar ${count} propostas · ${totalCredits} créditos`}
              </button>

              {loading && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.85rem', background: 'rgba(168,85,247,0.1)', borderRadius: '12px', border: '1px solid rgba(168,85,247,0.2)' }}>
                  <div style={{ width: 16, height: 16, border: '2px solid #a855f7', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                  <span style={{ fontSize: '0.8rem', color: '#c084fc' }}>{loadingStep}</span>
                </div>
              )}

              {error && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.85rem', background: 'rgba(239,68,68,0.1)', borderRadius: '12px', border: '1px solid rgba(239,68,68,0.2)', fontSize: '0.8rem', color: '#f87171' }}>
                  <AlertCircle size={16} /> {error}
                </div>
              )}
           </div>
        </div>
      </div>

      {previews.length > 0 && (
        <div className="glass-card" style={{ padding: '1.5rem', border: '1px solid rgba(34,197,94,0.3)' }}>
          <div style={{ fontSize: '1rem', fontWeight: 600, color: '#4ade80', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Layers size={18} /> Propostas geradas
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
            {previews.map((preview, i) => (
              <div key={`${preview.url}-${i}`} style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(168,85,247,0.45)', background: 'rgba(0,0,0,0.3)', position: 'relative' }}>
                <div style={{ position: 'absolute', top: 10, left: 10, background: '#7c3aed', padding: '0.2rem 0.6rem', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 'bold', color: '#fff', boxShadow: '0 2px 10px rgba(0,0,0,0.4)', zIndex: 2 }}>
                  {preview.source || selectedModel.label} {previews.length > 1 ? i + 1 : ''}
                </div>
                <img src={preview.url} alt={`Preview ${i + 1}`} style={{ width: '100%', display: 'block', objectFit: 'contain' }} />
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
